import React, { useEffect, useMemo, useState } from 'react'
import api from '../../api/client';
import {
  Card, CardHeader, CardContent, Divider, Grid,
  Typography, makeStyles
} from '@material-ui/core'
import { toFarsiDigits } from '../../assets/utils'
import { useRecoilValue, useRecoilState } from 'recoil'
import Layout from '../../components/layout/Layout'
import ConsultantInfo from '../../components/contractor/ConsultantInfo'
import StatsCards from '../../components/contracts/StatsCards'
import ContractCards from '../../components/contracts/ContractCards'
import SortSelect from '../../components/contracts/SortSelect'
import SearchField from '../../components/SearchField'
import { computeStats, filterRows, sortRows } from '../../components/contracts/boardUtils'
import { loadingState, userInfoState } from '../../recoil/atoms'
import RequestsModal from '../supervisor/RequestsModal'

const useStyles = makeStyles(() => ({
  root: { background: '#f1f1f1', flexGrow: 1 },
  container: { maxHeight: '60vh' },
  headerRow: { fontWeight: 700 },
  button: {
    background: '#8fa8f8',
    '&:hover': { background: '#3963f0' }
  }
}))

const extractResults = (res) => {
  if (!res) return []
  const d = res.data
  if (Array.isArray(d)) return d
  if (d && Array.isArray(d.results)) return d.results
  return []
}

export default function ConsultantDashboard({ onSwitchRole }) {
  const classes = useStyles()
  const [contracts, setContracts] = useState([])
  const [loading, setLoading] = useRecoilState(loadingState)
  const userInfo = useRecoilValue(userInfoState)
  const [err, setErr] = useState('')
  const [query, setQuery] = useState('')
  const [mode, setMode] = useState('all')
  const [sortMode, setSortMode] = useState('name')

  // Modal state - صورت‌وضعیت‌های قرارداد (مثلِ پنل ناظر)
  const [openReq, setOpenReq] = useState(false)
  const [selectedContract, setSelectedContract] = useState(null)

  // آمار و فیلترِ داشبورد
  const stats = useMemo(() => computeStats(contracts), [contracts])
  // مرتب‌سازی: اول پیمانکار، سپس در قراردادهای هر پیمانکار تاریخِ نزولی
  const sorted = useMemo(() => sortRows(contracts, sortMode), [contracts, sortMode])
  const visible = useMemo(
    () => filterRows(sorted, { query, mode }),
    [sorted, query, mode]
  )

  // Fetch contracts
  useEffect(() => {
    let unmounted = false
    const fetchData = async () => {
      setLoading(true)
      setErr('')
      try {
        const url = 'contracts/?consultant=me&page=1&page_size=300'
        const response = await api.get(url)
        if (!unmounted) {
          const items = extractResults(response)
          setContracts(items)
        }
      } catch (e) {
        if (!unmounted) setErr('خطا در دریافت لیست قراردادها')
      } finally {
        if (!unmounted) setLoading(false)
      }
    }
    fetchData()
    return () => { unmounted = true }
  }, [setLoading])

  const openDialogFor = (contract) => {
    setSelectedContract(contract)
    setOpenReq(true)
  }

  return (
    <Layout onSwitchRole={onSwitchRole}>
      <Grid container spacing={3}>
        {/* اطلاعات مشاور */}
        <Grid item xs={12}>
          <ConsultantInfo 
            userInfo={userInfo}
            setOpenRestrictions={() => {}}
          />
        </Grid>

        {/* جدول قراردادها */}
        <Grid item xs={12}>
          <Card elevation={5} className={classes.root}>
            <CardHeader
              title="قراردادهای تحت مشاوره من"
              action={
                <Typography variant="body2" style={{ color: '#5a6b8c' }}>
                  {toFarsiDigits(visible.length)} قرارداد نمایش داده می‌شود
                </Typography>
              }
            />
            <Divider />
            <CardContent>
              {loading && <Typography>در حال بارگذاری...</Typography>}
              {!!err && <Typography color="error">{err}</Typography>}
              {!loading && !err && (
                <>
                  <StatsCards stats={stats} mode={mode} onModeChange={setMode} />

                  <Grid container spacing={2} style={{ marginBottom: 14 }} alignItems="center">
                    <Grid item xs={12} md={7}>
                      <SearchField
                        size="small"
                        variant="outlined"
                        placeholder="🔍 جستجو در سازمان، شماره، موضوع، پیمانکار ... (همان لحظه)"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        style={{ minWidth: 280, width: '100%', maxWidth: 520, background: '#fff', borderRadius: 8 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <SortSelect value={sortMode} onChange={setSortMode} />
                    </Grid>
                  </Grid>

                  <ContractCards
                    rows={visible}
                    sortMode={sortMode}
                    onOpenRequests={openDialogFor}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* مودالِ صورت‌وضعیت‌ها (همانِ پنل ناظر) */}
      <RequestsModal
        open={openReq}
        onClose={() => setOpenReq(false)}
        contract={selectedContract}
      />
    </Layout>
  )
}